import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { retry, timer } from 'rxjs';

/**
 * HTTP Retry Interceptor with exponential backoff.
 *
 * Retries failed GET requests up to 2 times.
 * Does NOT retry:
 * - POST/PUT/PATCH/DELETE (non-idempotent)
 * - 4xx errors (client errors — retrying won't help)
 * - 401 (handled by auth interceptor)
 *
 * Backoff schedule:
 * - 1st retry: 400 ms
 * - 2nd retry: 800 ms
 * (~1.2s total — keeps two retries for flaky networks while surfacing a real
 * failure to the user quickly instead of after ~3s.)
 */

const MAX_RETRIES = 2;
const INITIAL_DELAY_MS = 400;

export const retryInterceptorFn: HttpInterceptorFn = (req, next) => {
  if (req.method !== 'GET') {
    return next(req);
  }

  return next(req).pipe(
    retry({
      count: MAX_RETRIES,
      delay: (error: HttpErrorResponse, retryCount: number) => {
        if (error.status >= 400 && error.status < 500) {
          throw error;
        }

        const delayMs = INITIAL_DELAY_MS * Math.pow(2, retryCount - 1);
        return timer(delayMs);
      },
    }),
  );
};
